import React, { useState, useEffect } from 'react';
import {
  Card,
  BlockStack,
  InlineStack,
  Text,
  Badge,
  Spinner,
  Banner,
  Button,
  DataTable
} from '@shopify/polaris';
import { adminApi } from '../../services/api/adminApi';

export const HealthDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [healthData, setHealthData] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchHealth = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.getHealthStatus();
      
      if (response.success) {
        setHealthData(response);
      } else {
        setError(response.error || 'Failed to fetch health status');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch health status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    
    if (autoRefresh) {
      const interval = setInterval(fetchHealth, 30000); // Refresh every 30 seconds
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  const getHealthBadge = (status) => {
    const statusMap = {
      healthy: { tone: 'success', children: 'Healthy' },
      warning: { tone: 'warning', children: 'Warning' },
      error: { tone: 'critical', children: 'Error' }
    };
    return <Badge {...(statusMap[status] || { children: status })} />;
  };

  const getScoreBadge = (score) => {
    if (score >= 80) return <Badge tone="success">{score}</Badge>;
    if (score >= 50) return <Badge tone="warning">{score}</Badge>;
    return <Badge tone="critical">{score}</Badge>;
  };

  const tableRows = healthData?.tenants?.map(tenant => [
    tenant.tenantName,
    tenant.environment,
    tenant.integrations?.frontend?.provider || '-',
    tenant.integrations?.backend?.provider || '-',
    getScoreBadge(tenant.score),
    getHealthBadge(tenant.status),
    `${tenant.errorRate}%`,
    tenant.provisionedServices?.toString() || '0'
  ]) || [];

  return (
    <BlockStack gap="loose">
      {error && (
        <Banner status="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      <Card>
        <BlockStack gap="loose">
          <InlineStack align="space-between">
            <Text variant="headingMd" fontWeight="bold">Health Monitoring</Text>
            <InlineStack gap="loose">
              <Button onClick={fetchHealth} loading={loading}>
                Refresh
              </Button>
            </InlineStack>
          </InlineStack>

          {loading && !healthData ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spinner size="large" />
            </div>
          ) : healthData ? (
            <BlockStack gap="loose">
              {/* Summary Cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px', marginTop: '24px' }}>
                <Card sectioned>
                  <BlockStack gap="tight" style={{ padding: '16px' }}>
                    <Text variant="bodySm" tone="subdued">Total Tenants</Text>
                    <Text variant="headingLg" fontWeight="bold">{healthData.summary.total}</Text>
                  </BlockStack>
                </Card>
                <Card sectioned>
                  <BlockStack gap="tight" style={{ padding: '16px' }}>
                    <Text variant="bodySm" tone="subdued">Healthy</Text>
                    <Text variant="headingLg" fontWeight="bold">
                      <Badge tone="success">{healthData.summary.healthy}</Badge>
                    </Text>
                  </BlockStack>
                </Card>
                <Card sectioned>
                  <BlockStack gap="tight" style={{ padding: '16px' }}>
                    <Text variant="bodySm" tone="subdued">Warning</Text>
                    <Text variant="headingLg" fontWeight="bold">
                      <Badge tone="warning">{healthData.summary.warning}</Badge>
                    </Text>
                  </BlockStack>
                </Card>
                <Card sectioned>
                  <BlockStack gap="tight" style={{ padding: '16px' }}>
                    <Text variant="bodySm" tone="subdued">Error</Text>
                    <Text variant="headingLg" fontWeight="bold">
                      <Badge tone="critical">{healthData.summary.error}</Badge>
                    </Text>
                  </BlockStack>
                </Card>
                <Card sectioned>
                  <BlockStack gap="tight" style={{ padding: '16px' }}>
                    <Text variant="bodySm" tone="subdued">Overall Score</Text>
                    <Text variant="headingLg" fontWeight="bold">
                      {getScoreBadge(healthData.summary.overallScore)}
                    </Text>
                  </BlockStack>
                </Card>
              </div>

              {/* Divider between KPI cards and Tenant Health Table */}
              <div style={{ height: '16px' }}></div>

              {/* Tenant Health Table */}
              {tableRows.length > 0 && (
                <Card sectioned>
                  <div style={{ padding: '24px' }}>
                    <BlockStack gap="loose">
                      <Text variant="headingMd" fontWeight="bold">Tenant Health Status</Text>
                      <DataTable
                        columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'numeric', 'numeric']}
                        headings={['Tenant', 'Environment', 'Frontend', 'Backend', 'Score', 'Status', 'Error Rate', 'Services']}
                        rows={tableRows}
                      />
                    </BlockStack>
                  </div>
                </Card>
              )}

              {tableRows.length === 0 && (
                <Card sectioned>
                  <Text tone="subdued">No tenant health data available.</Text>
                </Card>
              )}
            </BlockStack>
          ) : null}
        </BlockStack>
      </Card>
    </BlockStack>
  );
};

